import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import { Heading, Page, TextButton } from '@zillow/constellation-10';
import { IconChevronLeftFilled } from '@zillow/constellation-icons';

import { Property } from '../models';
import LeasesHeaderNav from '../components/LeasesHeaderNav';
import { FULLSTORY_EXCLUDE } from '../constants/analytics';
import { onPrepareForLeaseSigningMobileHandoff, useIsInMobileWebView } from '../utils/mobileHandoffFunctions';
import { MOBILE_HANDOFF_TYPES } from '../constants/stringConstants';
import { usePropertyInfoQuery } from '../utils/queries';
import { useLeasingUrlParams } from '../hooks/useLeasingUrlParams';

type Props = {
  children?: ReactNode;
};

export function LandlordNonPdpPage({ children }: Props) {
  const history = useHistory();
  const { propertyId } = useLeasingUrlParams();
  const isInMobileWebView = useIsInMobileWebView();

  const { data: propertyInfo, isError, isLoading } = usePropertyInfoQuery(propertyId);

  const property = useMemo(() => {
    if (!propertyInfo) return undefined;
    return new Property({
      ...propertyInfo,
      propertyId,
    });
  }, [propertyInfo, propertyId]);

  function onLinkClick() {
    history.push(Property.getLeasesDashboardPath(propertyId));
    onPrepareForLeaseSigningMobileHandoff(MOBILE_HANDOFF_TYPES.RETURN_TO_DASHBOARD);
  }

  function renderBreadcrumbText(): string {
    if (isLoading) {
      return '';
    }
    if (isError || !property?.streetAddress) {
      return 'Your leases';
    }
    return `Leases for ${property.streetAddress}`;
  }

  return (
    <LeasesHeaderNav>
      <Page.Root css={{ background: 'bg.neutral' }}>
        <Page.Breadcrumb>
          <TextButton
            tone="brand"
            className={`PDP-breadcrumb-link ${FULLSTORY_EXCLUDE}`}
            onClick={onLinkClick}
            icon={<IconChevronLeftFilled />}
            iconPosition="start"
          >
            {renderBreadcrumbText()}
          </TextButton>
        </Page.Breadcrumb>
        <Page.Header>
          <Heading level={1}>Leases</Heading>
        </Page.Header>
        <Page.Content>{children}</Page.Content>
      </Page.Root>
    </LeasesHeaderNav>
  );
}

export default LandlordNonPdpPage;
